/*
 * Description: 几何对象基础类，基于geojson格式
 * 点：{ "type": "Point", "coordinates": [30, 10] }
 * 线：{ "type": "LineString", "coordinates": [ [30, 10], [10, 30], [40, 40] ] }
 * 面：{ "type": "Polygon", "coordinates": [ [ [30, 10], [40, 40], [20, 40], [10, 20], [30, 10]  ] }
 * 环面：{ "type": "Polygon", "coordinates": [ [[35, 10], [45, 45], [15, 40], [10, 20], [35, 10]], [[20, 30], [35, 35], [30, 20], [20, 30]] ] }
 * 多点：{ "type": "MultiPoint", "coordinates": [ [10, 40], [40, 30], [20, 20], [30, 10] ] }
 * 多线：{ "type": "MultiLineString", "coordinates": [ [[10, 10], [20, 20], [10, 40]], [[40, 40], [30, 30], [40, 20], [30, 10]] ] }
 * 多面：{ "type": "MultiPolygon", "coordinates": [ [ [[30, 20], [45, 40], [10, 40], [30, 20]] ], [ [[15, 5], [40, 10], [10, 20], [5, 10], [15, 5]] ] ] }
 * 多面环面：{ "type": "MultiPolygon", "coordinates": [ [ [[40, 40], [20, 45], [45, 30], [40, 40]] ], [ [[20, 35], [10, 30], [10, 10], [30, 5], [45, 20], [20, 35]], [[30, 20], [20, 15], [20, 25], [30, 20]] ] ] }
 * Date: Create in 2021-01-28 15:35:34
 */

class Geometry {
    //几何类型字符串：可选值:['Point', 'LineString', 'Polygon', 'MultiPoint', 'MultiLineString', 'MultiPolygon', 'MultiPolygon']
    type = null;
    //坐标信息
    coordinates = [];
    //构造函数
    constructor(option) {
        if (option == null || option == undefined) return;
        if (option.type) this.type = option.type;
        if (option.coordinates) this.coordinates = option.coordinates;
    }
    /**
     * 输出geojson
     */
    toGeoJson() {
        return {
            type: this.type,
            coordinates: this.coordinates,
        }
    }

    /**
     * geojson转换为gml，用于OSGC标准的xml请求
     * @param {string} srs 坐标系
     * @example
     * srs='EPSG:4326'
     */
    toGML(srs) {
        //注意gml的坐标是反的y x
        switch (this.type) {
            case 'Point':
                return this._geoJson2Point(this, srs);
            case 'LineString':
                return this._geoJson2LineString(this, srs);
            case 'MultiLineString':
                return this._geoJson2MultiLineString(this, srs);
            case 'Polygon': //暂时不支持复杂多边形
                return this._geoJson2Polygon(this, srs);
            default:
                new Error("不支持的几何类型");
        }
    }

    /**
     *将geojson格式的简单点转换为gml对象
     * @param {object} geoJson 点的geojson对象
     * @param {string} srs 坐标系
     */
    _geoJson2Point(geoJson, srs) {
        return {
            'gml:Point': {
                'gml:pos': geoJson.coordinates[1] + " " + geoJson.coordinates[0],
                'attr': {
                    'srsName': "urn:ogc:def:crs:" + srs
                }
            }
        };
    }

    /**
     * 将geojson格式的简单线转换为gml对象
     * @param {object} geoJson 线（LineString）的geojson对象
     * @param {string} srs 坐标系
     */
    _geoJson2LineString(geoJson, srs) {
        var reversArr = [];
        geoJson.coordinates.forEach(x => {
            reversArr.push(x.slice(0, 2).reverse())
        });
        return {
            'gml:LineString': {
                'gml:posList': reversArr.reduce((a, b) => a.concat(b)).join(' '),
                'attr': {
                    'srsName': "urn:ogc:def:crs:" + srs
                }
            }
        }
    }

    /**
     * 将geojson格式的多线转换为gml对象
     * @param {object} geoJson 多线(MultiLineString)的geojson对象
     * @param {string} srs 坐标系
     */
    _geoJson2MultiLineString(geoJson, srs) {
        return {
            'gml:MultiCurve': {
                'gml:curveMember': geoJson.coordinates.map(path => {
                    var reversArr = [];
                    path.forEach(x => {
                        reversArr.push(x.slice(0, 2).reverse())
                    });
                    var posList = reversArr.reduce((a, b) => a.concat(b)).join(' ')
                    return {
                        'gml:LineString': {
                            'gml:posList': posList
                        }
                    }
                }),
                'attr': {
                    'srsName': "urn:ogc:def:crs:" + srs
                }
            }
        }
    }

    /**
     * 将geojson格式的简单多边形转换为gml对象
     * @param {object} geoJson 简单面（Polygon）的geojson对象
     * @param {string} srs 坐标系
     */
    _geoJson2Polygon(geoJson, srs) {
        var reversArr = [];
        geoJson.coordinates[0].forEach(x => {
            reversArr.push(x.slice(0, 2).reverse())
        });
        return {
            'gml:Polygon': {
                'gml:exterior': {
                    'gml:LinearRing': {
                        'gml:posList': reversArr.reduce((a, b) => a.concat(b)).join(' '),
                    }
                },
                'attr': {
                    'srsName': "urn:ogc:def:crs:" + srs
                }
            }
        }
    }
};

/**
 * 几何点基础类
 * @extends Geometry
 */
class Point extends Geometry {
    _x = NaN;
    _y = NaN;
    /**
     * 构造方法
     * @param {number} x 横坐标或经度
     * @param {number} y 纵坐标或维度
     */
    constructor(x, y) {
        super();
        this._x = x;
        this._y = y;
        this.coordinates = [x, y];
        this.type = 'Point'
    };
};

/**
 * 几何线基础类
 * @extends Geometry
 */
class Polyline extends Geometry {
    /**
     * 构造方法
     * @param  {...any} points 坐标点集合
     */
    constructor(...points) {
        super();
        this.type = 'MultiLineString';
        this.coordinates = points;
    }
};

const XmlUtil = {
    /**
     *json转为xml，用于soap请求
     * @param {*} json
     * @param {*} opts
     * @param {*} pkey
     */
    json2xml: function (json, opts, pkey) {

        var that = this;
        var settings = {
            attributes_key: false,
            header: false
        };
        if (opts) {
            Object.keys(settings).forEach(function (k) {
                if (opts[k] === undefined) {
                    opts[k] = settings[k];
                }
            });
        } else {
            opts = settings;
        }

        var result = opts.header ? '<?xml version="1.0" encoding="UTF-8"?>' : '';
        opts.header = false;

        if (json instanceof Array) { //Array
            json.forEach(function (node, index) {
                if (index > 0 && pkey)
                    result += (that._format("</%s><%s>", pkey, pkey) + that.json2xml(node, opts));
                else result += that.json2xml(node, opts);
            });
        } else if (typeof json === 'object') {
            Object.keys(json).forEach(function (key) {
                if (key !== opts.attributes_key) {
                    var node = json[key],
                        attributes = '';

                    if (node === undefined || node === null) {
                        node = '';
                    }

                    if (opts.attributes_key && json[key] != null && json[key][opts.attributes_key]) {
                        Object.keys(json[key][opts.attributes_key]).forEach(function (k) {
                            attributes += that._format(' %s="%s"', k, json[key][opts.attributes_key][k]);
                        });
                    }

                    if (json[key] instanceof Array) {
                        json[key].forEach((node) => {
                            attributes = '';
                            if (opts.attributes_key && node[opts.attributes_key])
                                Object.keys(node[opts.attributes_key]).forEach(function (k) {
                                    attributes += that._format(' %s="%s"', k, node[opts.attributes_key][k]);
                                });
                            result += that._format("<%s%s>%s</%s>", key, attributes, that.json2xml(node, opts, key), key);
                        });
                    } else {
                        var inner = that.json2xml(node, opts);

                        if (inner) {
                            result += that._format("<%s%s>%s</%s>", key, attributes, that.json2xml(node, opts, key), key);
                        } else {
                            result += that._format("<%s%s/>", key, attributes);
                        }
                    }
                }
            });
        } else {
            if (json.toString().match(/^<!\[CDATA\[.*]]>$/)) {
                return json.toString();
            }
            return json.toString().replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
        }

        return result;
    },
    /**
     * 字符串格式化工具
     * @param {*} f
     */
    _format: function (f) {
        var i = 1;
        var args = arguments;
        var len = args.length;
        var formatRegExp = /%[sdj%]/g;

        var str = String(f).replace(formatRegExp, function (x) {
            if (x === '%%') return '%';
            if (i >= len) return x;
            switch (x) {
                case '%s':
                    return String(args[i++]);
                case '%d':
                    return Number(args[i++]);
                case '%j':
                    try {
                        return JSON.stringify(args[i++]);
                    } catch (_) {
                        return '[Circular]';
                    }
                    default:
                        return x;
            }
        });
        return str;
    }
};

import Init from './Init'
import axiosClass from 'axios'
const axios = axiosClass.create()
/*
 * Author:wangjiao
 * Description: geoserver的WFS数据编辑操作库
 * Date: Create in 2021-01-28 17:26:35
 */
class WFST {
    //geoserver主机地址
    service = '/geoserver';
    //工作空间
    workspace = '';
    //ogc相关配置项
    options = {
        VERSION_WFS: '2.0.0',
        SRS_CLIENT: 'EPSG:4326',
        GEOField: 'geom',
        maxbuffer: 100
    };

    // mapinfo = null; //暂存地图基础信息，需要的时候才从geoserver获取具体信息
    getMapinfoPromise = null;

    /**
     * 构造方法
     * @param {String} service GeoServer服务器地址，示例：http://localhost:8085/geoserver
     * @param {String} workspace GeoServer工作空间，示例：demo
     * @param {Object} options 选项配置
     */
    constructor(service = '/geoserver', workspace = '', options = {}) {
        if (!service) throw '必须指定GeoServer服务器地址，示例：http://localhost:8080/geoserver'
        this.service = service;
        this.workspace = workspace;
        Object.assign(this.options, options);
    }

    /**
     * 使用权限认证，传入用户名，密码，以使用geoserver的数据编辑接口
     * （暂时采用Basic认证方式，使用时配合https更安全）
     * @param {string} user 用户名 
     * @param {*} pwd 密码
     */
    useAuth(user, pwd) {
        //当前实例内的wfs请求都增加认证
        axios.defaults.auth = {
            username: user,
            password: pwd
        }
    }

    /**
     * 创建地图要素
     *
     * @fires GISUtil#progress:loading
     * @fires GISUtil#progress:loaded
     *
     * @param {Object|Array} feature:
     * [{
     *  name:'fitting'//图层名
     *  features:[{//该图层需要新增的要素集合
     *      properties:{name:'',x:'',y:''}//属性对象,具体属性由gis数据结构决定
     *      geometry:{//几何图形的geojson格式对象
     *          type:'',//几何类型，可取值：Point,LineString,MultiLineString,Polygon,MultiPoint,MultiPolygon
     *          coordinates:[],//坐标点集合
     *          }
     *     }]
     * }]
     * @returns {Promise} 创建结果
     */
    createFeature(feature) {
        if (!feature) return Promise.resolve();
        let features = Array.isArray(feature) ? feature : [feature];
        var that = this;
        return new Promise(function (resolve, reject) {
            // 一段耗时的异步操作
            var url = that.service + "/wfs?exceptions=json";
            var xmlInsert = that._getInsertFeatureXML(features);
            axios.post(url, xmlInsert, {
                headers: {
                    'Content-Type': 'text/xml'
                },
                timeout: 10000
            }).then(response => {
                resolve({
                    data: that._getEditResponse(response.data) //.data.features
                });
            }).catch(error => {
                if (error.response.data.exceptions && error.response.data.exceptions.length > 0)
                    reject(new Error(error.response.data.exceptions[0].text));
                else reject(error);
            });
        })
    }

    createFeaturePromise(feature, resolve, reject) {
        if (!feature) resolve();
        let features = Array.isArray(feature) ? feature : [feature];
        var that = this;
        // 一段耗时的异步操作
        var url = that.service + "/wfs?exceptions=json";
        var xmlInsert = that._getInsertFeatureXML(features);
        axios.post(url, xmlInsert, {
            headers: {
                'Content-Type': 'text/xml'
            },
            timeout: 10000
        }).then(response => {
            resolve({
                data: that._getEditResponse(response.data) //.data.features
            });
        }).catch(error => {
            if (error.response.data.exceptions && error.response.data.exceptions.length > 0)
                reject(new Error(error.response.data.exceptions[0].text));
            else reject(error);
        });
    }

    /**
     * 通过地图要素ID删除要素
     * @param {String|Array} featureIds 需要删除的要素的ID集合，数组
     * @returns {Promise} 操作结果
     */
    removeFeature(featureIds) {
        if (typeof featureIds === 'undefined' || !(featureIds instanceof Array))
            return Promise.resolve();
        var that = this;
        return new Promise(function (resolve, reject) {
            // 一段耗时的异步操作
            var url = that.service + "/wfs?exceptions=json";
            var xmlInsert = that._getIDRemoveFeatureXML(featureIds);
            axios.post(url, xmlInsert, {
                headers: {
                    'Content-Type': 'text/xml'
                }
            }).then(response => {
                resolve({
                    data: that._getEditResponse(response.data) //.data.features
                });
            }).catch(error => {
                if (error.response.data.exceptions && error.response.data.exceptions.length > 0)
                    reject(new Error(error.response.data.exceptions[0].text));
                else reject(error);
            });
        })
    }
    /**
     * 通过筛选条件移除要素
     * @param {Object|Array} condition 筛选条件(或集合)
     * @param {string} condition.layer 图层类型
     * @param {object} [condition.geometry] 空间范围条件
     * @param {Array} [condition.where] 属性条件，用And连接多个条件
     * @param {string} condition.where[].key 属性名
     * @param {object} condition.where[].value 属性值
     * @returns {Promise} 操作结果
     */
    removeFeatureByCondition(condition) {
        if (typeof filters === 'undefined')
            return Promise.resolve();
        let filters = Array.isArray(condition) ? condition : [condition];
        var that = this;
        return new Promise(function (resolve, reject) {
            // 一段耗时的异步操作
            var url = that.service + "/wfs?exceptions=json";
            var xmlInsert = that._getRemoveFeatureXML(filters);
            axios.post(url, xmlInsert, {
                headers: {
                    'Content-Type': 'text/xml'
                }
            }).then(response => {
                resolve({
                    data: that._getEditResponse(response.data) //.data.features
                });
            }).catch(error => {
                if (error.response.data.exceptions && error.response.data.exceptions.length > 0)
                    reject(new Error(error.response.data.exceptions[0].text));
                else reject(error);
            });
        })
    }

    /**
     * 更新要素
     * @param {object|Array} feature 要素(集合)
     * @param {string} feature.id 要素id
     * @param {object} [feature.properties] 属性对象
     * @param {object} [feature.geometry] 几何对象
     * @example
     * [
     *  {
     *      id:'',//要素id
     *      properties:{},//{name:'',x:'',y:''}//属性对象,可选参数,具体属性由gis数据结构决定
     *      geometry:{//几何图形的geojson格式对象
     *          type:'',//几何类型，可取值：Point,LineString,MultiLineString,Polygon,MultiPoint,MultiPolygon
     *          coordinates:[],//坐标点集合
     *          }
     * }
     * ]
     */
    updateFeature(feature) {
        if (!feature) return Promise.resolve();
        let features = Array.isArray(feature) ? feature : [feature];
        var that = this;
        return new Promise(function (resolve, reject) {
            // 一段耗时的异步操作
            var url = that.service + "/wfs?exceptions=json";
            var xmlUpdate = that._getUpdateFeatureXML(features);
            //axios
            axios.post(url, xmlUpdate, {
                headers: {
                    'Content-Type': 'text/xml'
                }
            }).then(response => {
                resolve({
                    //转换xml to json
                    data: that._getEditResponse(response.data) //.data.features
                });
            }).catch(error => {
                if (error.response.data.exceptions && error.response.data.exceptions.length > 0)
                    reject(new Error(error.response.data.exceptions[0].text));
                else reject(error);
            });
        })
    }

    /**
     * 按过滤条件更新要输属性
     * @param {string} layertype 图层
     * @param {object} valuses 属性对象 
     * @param {object} [filter] 过滤条件,缺失时表示修改该图层所有数据
     * @return {Promise}
     * @example
     * 暂时采用如下格式,并用And连接
     * let filter={
     *     where:[
     *         {
     *             key:'',
     *             value:''
     *        },{
     *             key:'',
     *             value:''
     *         }
     *     ]
     *  }
     */
    updateFeatureByCondition(layertype, values, filter) {
        let self = this;
        return new Promise(function (resolve, reject) {
            // 一段耗时的异步操作
            var url = self.service + "/wfs?exceptions=json";
            var xmlUpdata = self._getUpdateFeatureByConditionXML(layertype, values, filter);
            axios.post(url, xmlUpdata, {
                headers: {
                    'Content-Type': 'text/xml'
                }
            }).then(response => {
                resolve({
                    data: self._getEditResponse(response.data) //.data.features
                });
            }).catch(error => {
                if (error.response.data.exceptions && error.response.data.exceptions.length > 0)
                    reject(new Error(error.response.data.exceptions[0].text));
                else reject(error);
            });
        })
    }

    //#region 编辑

    /**
     * 添加要素到当前编辑会话中
     * @param {string} layertype 图层类型
     * @param {object} geometry geojson规范的几何对象
     * @param {object} [properties] 属性对象
     */
    addFeature(layertype, geometry, properties) {
        return this.createFeature([{
            name: layertype,
            features: [{
                properties: properties,
                geometry: geometry
            }]
        }]);
    }

    /**
     *添加要素集合到当前编辑会话中
     * @param {object[]} featuresets 要素集合
     * @param {string} featuresets[].name 图层名
     * @param {object[]} featuresets[].features 要素集合
     * @param {object} featuresets[].features[].properties 要素属性
     * @param {object} featuresets[].features[].geometry 要素几何
     * @example
     * addFeatures(
     *  [{
     *  name:'fitting'//图层名
     *  features:[{//该图层需要新增的要素集合
     *      properties:{name:'',x:'',y:''}//属性对象,具体属性由gis数据结构决定
     *      geometry:{//几何图形的geojson格式对象
     *          type:'',//几何类型，可取值：Point,LineString,MultiLineString,Polygon,MultiPoint,MultiPolygon
     *          coordinates:[],//坐标点集合
     *          }
     * }]
     * );
     */
    addFeatures(featuresets) {
        if (typeof featuresets === 'undefined' || !(featuresets instanceof Array))
            return Promise.resolve();
        return this.createFeature(featuresets);
    }
    //#region

    //#region private

    /**
     * 计算容差多边形
     * @param {x坐标} x
     * @param {y坐标} y
     * @param {容差} toler
     */
    _getToler(x, y, toler = 0.00021499999999718966) {
        var xmin = x - toler;
        var ymin = y - toler;
        var xmax = x + toler;
        var ymax = y + toler;
        var path = [];
        path.push([xmin, ymin]);
        path.push([xmax, ymin]);
        path.push([xmax, ymax]);
        path.push([xmin, ymax]);
        path.push([xmin, ymin]);
        return {
            type: 'Polygon',
            coordinates: [path]
        }
    }
    /**
     * 构建查询用的Query参数
     */
    _getQuery(layer, crs, geometry, tolerance) {
        // layer:{
        //     layertype,
        //     where:[{
        //         key,
        //         value
        //         }...]
        //     },
        var operate = 'fes:Intersects';
        var query = {
            'fes:Filter': {},
            'attr': {
                'srsName': "urn:ogc:def:crs:" + crs,
                'typeNames': this.workspace + ":" + layer.layertype
            }
        };
        var proFilter = [];
        if (layer.where != null && layer.where instanceof Array) {
            //构建属性过滤条件
            layer.where.forEach(item => {
                proFilter.push({
                    'fes:ValueReference': item.key,
                    'fes:Literal': item.value
                });
            });
        }
        if (geometry != null) {
            var gmlData = null;
            if (geometry.type == "Point") {
                var env = this._getToler(geometry.coordinates[0], geometry.coordinates[1], tolerance);
                gmlData = new Geometry(env).toGML(this.options.SRS_CLIENT);
            } else
                gmlData = new Geometry(geometry).toGML(this.options.SRS_CLIENT);
            query['fes:Filter'][operate] = {
                'fes:ValueReference': this.options.GEOField,
            }
            var key = Object.keys(gmlData)[0];
            if (proFilter.length == 0)
                query['fes:Filter'][operate][key] = gmlData[key];
            else query['fes:Filter']['And'][operate][key] = gmlData[key];
        } else {
            if (proFilter.length > 1)
                query['fes:Filter']['And']['fes:PropertyIsEqualTo'] = proFilter;
            else if (proFilter.length == 1)
                query['fes:Filter']['fes:PropertyIsEqualTo'] = proFilter;
        }
        return query
    }
    /**
     * 构建完整的查询用的GetFeature参数，支持空间和属性过滤
     * @param {查询参数集} options
     */
    _getGetFeatureXML(options) {
        var data = {
            'wfs:GetFeature': {
                'wfs:Query': options.layers.map(layer => this._getQuery(layer, this.options.SRS_CLIENT, options.geometry, options.tolerance)),
                'attr': {
                    'services': 'WFS',
                    'version': this.options.VERSION_WFS,
                    'outputFormat': 'json',
                    'xmlns:wfs': "http://www.opengis.net/wfs/2.0",
                    'xmlns:fes': "http://www.opengis.net/fes/2.0",
                    'xmlns:gml': "http://www.opengis.net/gml/3.2",
                    'xmlns:sf': "http://www.openplans.org/spearfish",
                    'xmlns:xsi': "http://www.w3.org/2001/XMLSchema-instance",
                    'xsi:schemaLocation': `http://www.opengis.net/wfs/2.0
                        http://schemas.opengis.net/wfs/2.0/wfs.xsd
                        http://www.opengis.net/gml/3.2
                        http://schemas.opengis.net/gml/3.2.1/gml.xsd`
                }
            }
        };
        var xml = XmlUtil.json2xml(data, {
            attributes_key: 'attr',
            header: true
        });
        return xml;
    }

    _getUpdateByCondition(layertype, values, filter) {
        if (layertype == null || layertype == undefined) throw '缺少layertype';
        var update = {
            'attr': {
                'typeName': this.workspace + ':' + layertype
            },
            'wfs:Property': (typeof values === 'undefined') ? [] : Object.keys(values).filter(key => values[key] !== undefined).map(key => {
                if (values[key] == null || (typeof values[key] == 'string' && values[key].trim().length == 0)) //设置字段值为null
                    return {
                        'wfs:ValueReference': key,
                    }
                else
                    return {
                        'wfs:ValueReference': key,
                        'wfs:Value': values[key]
                    }
            })
        };
        if (filter != null && filter != undefined && filter.where instanceof Array && filter.where.length > 0) {
            update['fes:Filter'] = filter.where.length == 1 ? {
                'fes:PropertyIsEqualTo': {
                    'fes:ValueReference': filter.where[0].key,
                    'fes:Literal': filter.where[0].value
                }
            } : {
                'And': {
                    'fes:PropertyIsEqualTo': filter.where.map(item => {
                        return {
                            'fes:ValueReference': item.key,
                            'fes:Literal': item.value
                        }
                    })
                }
            }
        }
        return update;
    }

    /**
     * 构建单个要素的更新参数
     * @param {需要更新的要素，包含ID信息，可选空间和属性信息} feature
     */
    _getUpdate(feature) {
        if (feature.id == null || feature.id == undefined) throw '缺少id';
        var update = {
            'attr': {
                'typeName': this.workspace + ':' + feature.id.split('.')[0]
            },
            'fes:Filter': {
                'fes:ResourceId': {
                    'attr': {
                        'rid': feature.id
                    }
                },
            },
            'wfs:Property': (typeof feature.properties === 'undefined') ? [] : Object.keys(feature.properties).filter(key => feature.properties[key] !== undefined).map(key => {
                if (feature.properties[key] == null || (typeof feature.properties[key] == 'string' && feature.properties[key].trim().length == 0)) //设置字段值为null
                    return {
                        'wfs:ValueReference': key,
                    }
                else
                    return {
                        'wfs:ValueReference': key,
                        'wfs:Value': feature.properties[key]
                    }
            })
        };
        if (feature.geometry != null && feature.geometry != undefined)
            update['wfs:Property'].push({
                'wfs:ValueReference': this.options.GEOField,
                'wfs:Value': feature.geometry.toGML ? feature.geometry.toGML(this.options.SRS_CLIENT) : new Geometry(feature.geometry).toGML(this.options.SRS_CLIENT)
            });
        return update;
    }

    /**
     * 获取wfs Transaction相关命名空间
     */
    _getTransactionAtt() {
        return {
            'services': 'WFS',
            'version': this.options.VERSION_WFS,
            'outputFormat': 'json',
            'xmlns:wfs': "http://www.opengis.net/wfs/2.0",
            'xmlns:fes': "http://www.opengis.net/fes/2.0",
            'xmlns:gml': "http://www.opengis.net/gml/3.2",
            'xmlns:xsi': "http://www.w3.org/2001/XMLSchema-instance",
            'xsi:schemaLocation': `http://www.opengis.net/wfs/2.0
                    http://schemas.opengis.net/wfs/2.0/wfs.xsd
                    http://www.opengis.net/gml/3.2`
        }
    }
    _getUpdateFeatureByConditionXML(layertype, values, filter) {
        var data = {
            'wfs:Transaction': {
                'wfs:Update': this._getUpdateByCondition(layertype, values, filter),
                'attr': this._getTransactionAtt()
            }
        };
        var xml = XmlUtil.json2xml(data, {
            attributes_key: 'attr',
            header: true
        });
        return xml;
    }
    /**
     * 构建完整的多个要素的更新参数
     * @param {需要更新的要素集合} features
     */
    _getUpdateFeatureXML(features) {
        var data = {
            'wfs:Transaction': {
                'wfs:Update': features.map(feature => this._getUpdate(feature)),
                'attr': this._getTransactionAtt()
            }
        };
        var xml = XmlUtil.json2xml(data, {
            attributes_key: 'attr',
            header: true
        });
        return xml;
    }
    /**
     * 构建多个要素的新增参数，处理gml转换
     * @param {需要新增的要素集合} featuresets
     */
    _getInsert(featuresets) {
        var insert = {};
        featuresets.forEach(featureset => {
            insert[featureset.name] = featureset.features.map(feature => {
                var newFeature = {};
                newFeature[this.options.GEOField] = feature.geometry.toGML ? feature.geometry.toGML(this.options.SRS_CLIENT) : new Geometry(feature.geometry).toGML(this.options.SRS_CLIENT);
                //移除空字段,否则提交insert会错误
                Object.keys(feature.properties).forEach(key => {
                    if (feature.properties[key] == null)
                        delete feature.properties[key];
                });
                return Object.assign(newFeature, feature.properties);
            });
        });
        return insert;
    }
    /**
     * 构建完整的多个要素的新增参数
     * @param {需要新增的要素集合} featureset
     */
    _getInsertFeatureXML(featureset) {
        var data = {
            'wfs:Transaction': {
                'wfs:Insert': {},
                'attr': Object.assign({
                    'xmlns': `http://geoserver.org/${this.workspace}`
                }, this._getTransactionAtt())
            }
        };
        data['wfs:Transaction']['wfs:Insert'] = this._getInsert(featureset);
        var xml = XmlUtil.json2xml(data, {
            attributes_key: 'attr',
            header: true
        });
        return xml;
    }

    /**
     * 构建基于ID集合的删除参数
     * @param {需要删除的要素ID集合}} ids
     */
    _getIDRemoveFeatureXML(ids) {
        var data = {
            'wfs:Transaction': {
                'wfs:Delete': ids.map(id => {
                    return {
                        'attr': {
                            'typeName': this.workspace + ':' + id.split('.')[0]
                        },
                        'fes:Filter': {
                            'fes:ResourceId': {
                                'attr': {
                                    'rid': id
                                }
                            }
                        }
                    }
                }),
                'attr': this._getTransactionAtt()
            }
        };
        var xml = XmlUtil.json2xml(data, {
            attributes_key: 'attr',
            header: true
        });
        return xml;
    }

    /**
     * 构建基于自定义查询条件的删除参数
     * @param {*} filter
     */
    _getLayerRemoveFeatureXML(filter) {
        var data = {
            'attr': {
                'typeName': this.workspace + ':' + filter.layer
            },
            'fes:Filter': {}
        };
        var conditionCount = 0;
        if (typeof filter.geometry != 'undefined')
            conditionCount++;
        if (filter.where instanceof Array)
            conditionCount += filter.where.length;
        var geofilter = null;
        if (typeof filter.geometry != 'undefined') {
            geofilter = {
                'fes:Intersects': {
                    'fes:ValueReference': this.options.GEOField
                }
            }
            var gmlData = filter.geometry.toGML ? filter.geometry.toGML(this.options.SRS_CLIENT) : new Geometry(filter.geometry).toGML(this.options.SRS_CLIENT);
            var key = Object.keys(gmlData)[0];
            geofilter['fes:Intersects'][key] = gmlData[key];
        }
        var proFilter = [];
        if (filter.where instanceof Array) {
            filter.where.forEach(item => {
                proFilter.push({
                    'fes:ValueReference': item.key,
                    'fes:Literal': item.value
                });
            });
        }
        if (conditionCount == 1) {
            if (geofilter != null)
                data['fes:Filter']['fes:Intersects'] = geofilter['fes:Intersects'];
            else
                data['fes:Filter']['fes:PropertyIsEqualTo'] = proFilter[0];
        } else if (conditionCount > 1) {
            if (proFilter.length > 0)
                data['fes:Filter']['And']['fes:PropertyIsEqualTo'] = proFilter;
            if (geofilter != null)
                data['fes:Filter']['And']['fes:Intersects'] = geofilter['fes:Intersects'];
        }
        return data;
    }

    /**
     * 构建基于空间和属性条件的删除参数
     * @param {*} filter
     */
    _getRemoveFeatureXML(filters) {
        var data = {
            'wfs:Transaction': {
                'wfs:Delete': filters.map(filter => this._getLayerRemoveFeatureXML(filter)),
                'attr': this._getTransactionAtt()
            }
        };
        var xml = XmlUtil.json2xml(data, {
            attributes_key: 'attr',
            header: true
        });
        return xml;
    }

    /**
     * 解析geoserver返回的数据编辑接口相关xml格式结果
     * @param {数据编辑接口相关xml格式字符串} xml
     */
    _getEditResponse(xml) {
        var xmlDoc = this._getXMLDoc(xml);
        var ridNodes = xmlDoc.getElementsByTagName("fes:ResourceId");
        var rids = [];
        for (var i = 0, len = ridNodes.length; i < len; i++) {
            rids.push(ridNodes[i].getAttribute("rid"));
        }
        var result = {
            summary: {
                totalInserted: parseInt(xmlDoc.getElementsByTagName("wfs:totalInserted")[0].childNodes[0].nodeValue),
                totalUpdated: parseInt(xmlDoc.getElementsByTagName("wfs:totalUpdated")[0].childNodes[0].nodeValue),
                totalReplaced: parseInt(xmlDoc.getElementsByTagName("wfs:totalReplaced")[0].childNodes[0].nodeValue),
                totalDeleted: parseInt(xmlDoc.getElementsByTagName("wfs:totalDeleted")[0].childNodes[0].nodeValue),
            },
            ids: rids
        }
        result.count = result.summary.totalDeleted + result.summary.totalInserted + result.summary.totalReplaced + result.summary.totalUpdated;
        return result;
    }

    /**
     * 获取解析xml的工具
     * @param {xml格式的字符串}} txt
     */
    _getXMLDoc(txt) {
        if (typeof txt != 'string') throw '输入的不是XML';
        if (window.DOMParser) {
            var parser = new DOMParser();
            return parser.parseFromString(txt, "text/xml");
        }
        //#endregion
    }


    /**
     * 添加点要素
     * @param {object|Array} data 点要素对象(或集合)
     * @param {boolean} [data.fixed=false] 是否使用矢量缩放,即要素是否按地图单位(米)绘制-会在size参数上加m作为米的单位后缀，否则按像素单位绘制
     * @param {number} data.lng 经度或横坐标
     * @param {number} data.lat 维度或纵坐标
     * @param {string} data.type 设备类型id
     * @param {object} [data.property] 属性对象
     * @param {object} [data.style] 样式对象
     * @param {string} [data.style.color] 16进制颜色值字符串
     * @param {number} [data.style.size] 大小
     * @returns {Promise} 返回新建点的结果
     * @example
     * let data = {
     *  fixed:false,
     *  lng:105,
     *  lat:30,
     *  type:'',
     *  property:{
     *    status:0,
     *  },
     *  style:{
     *    color:'#ff0000',
     *    size:20,
     *  }
     * }
     * addPoint(data);
     */
    addPoint(data) {
        let newDatas = Array.isArray(data) ? data : [data];
        if (data == null || data == undefined || newDatas.length == 0) return Promise.resolve();
        let self = this;
        return new Promise(function (resolve, reject) {
            self.getMapinfo().then(mapinfo => {
                let layertype = self._findLayerbyType(mapinfo.layers, 'Point').layertype;
                if (layertype != null) {
                    try {
                        //格式化数据
                        let formatedatas = newDatas.map(data => self._formatPoint(data));
                        let featureset = {
                            name: layertype,
                            features: formatedatas
                        };
                        self.createFeaturePromise([featureset], resolve, reject);
                    } catch (error) {
                        reject(error);
                    }
                } else reject('创建要素时未匹配到图层');
            }).catch(() => {
                reject("获取图层信息失败")
            });
        });
    }

    /**
     * 添加线要素
     * @param {object|Array} data 线要素对象(或集合)
     * @param {boolean} [data.fixed=true] 是否使用矢量缩放,即要素是否按地图单位(米)绘制-会在size参数上加m作为米的单位后缀，否则按像素单位绘制
     * @param {object[]} data.path 坐标点集合,长度大于1
     * @param {number} data.path[][0] 经度或横坐标
     * @param {number} data.path[][1] 维度或纵坐标
     * @param {string} data.type 设备类型id
     * @param {object} [data.property] 属性对象
     * @param {object} [data.style] 样式对象
     * @param {string} [data.style.color] 线颜色,16进制颜色值字符串
     * @param {number} [data.style.size] 线宽
     * @returns {object} 返回格式化后点对象
     * @example
     * let data = {
     *  fixed:false,
     *  path:[[103,30],[103,31]]
     *  type:'',
     *  property:{
     *    status:0,
     *  },
     *  style:{
     *    color:'#ff0000',
     *    size:20,
     *  }
     * }
     * addPolyline(data);
     */
    addPolyline(data) {
        let newDatas = Array.isArray(data) ? data : [data];
        if (data == null || data == undefined || newDatas.length == 0) return Promise.resolve();
        let self = this;
        return new Promise(function (resolve, reject) {
            self.getMapinfo().then(mapinfo => {
                let layertype = self._findLayerbyType(mapinfo.layers, 'Line').layertype;
                if (layertype != null) {
                    try {
                        //格式化数据
                        let formatedatas = newDatas.map(data => self._formatPolyline(data));
                        let featureset = {
                            name: layertype,
                            features: formatedatas
                        };
                        self.createFeaturePromise([featureset], resolve, reject);
                    } catch (error) {
                        reject(error);
                    }
                } else reject('创建要素时未匹配到图层');
            }).catch(() => {
                reject("获取图层信息失败")
            });
        });
    }


    /**
     * 更新点要素(或集合)
     * @param {object|Array} data 点要素对象(或集合)
     * @param {string} data.id 要素id
     * @param {boolean} [data.fixed=false] 是否使用矢量缩放,即要素是否按地图单位(米)绘制-会在size参数上加m作为米的单位后缀，否则按像素单位绘制
     * @param {number} [data.lng] 经度或横坐标
     * @param {number} [data.lat] 维度或纵坐标
     * @param {string} [data.type] 设备类型id
     * @param {object} [data.property] 属性对象
     * @param {object} [data.style] 样式对象
     * @param {string} [data.style.color] 16进制颜色值字符串
     * @param {number} [data.style.size] 大小
     * @returns {Promise} 返回新建点的结果
     * @example
     * let data = {
     *  id:'fitting.8',
     *  fixed:false,
     *  lng:105,
     *  lat:30,
     *  type:'',
     *  property:{
     *    status:0,
     *  },
     *  style:{
     *    color:'#ff0000',
     *    size:20,
     *  }
     * }
     * updatePoints(data)或updatePoints([data]);
     */
    updatePoint(data) {
        if (!data) return Promise.resolve();
        try {
            let datas = Array.isArray(data) ? data : [data];
            let formatedDatas = datas.map(item => this._formatUpdatePoint(item));
            return this.updateFeature(formatedDatas);
        } catch (error) {
            return Promise.reject(error);
        }
    }

    /**
     * 更新线要素(或集合)
     * @param {object|Array} data 线要素对象(或集合)
     * @param {string} data.id 要素id
     * @param {boolean} [data.fixed=true] 是否使用矢量缩放,即要素是否按地图单位(米)绘制-会在size参数上加m作为米的单位后缀，否则按像素单位绘制
     * @param {object[]} [data.path] 坐标点集合,长度大于1
     * @param {number} [data.path[][0]] 经度或横坐标
     * @param {number} [data.path[][1]] 维度或纵坐标
     * @param {string} [data.type] 设备类型id
     * @param {object} [data.property] 属性对象
     * @param {object} [data.style] 样式对象
     * @param {string} [data.style.color] 线颜色,16进制颜色值字符串
     * @param {number} [data.style.size] 线宽
     * @returns {object} 返回格式化后点对象
     * @example
     * let data = {
     *  id:'pipe.8',
     *  fixed:false,
     *  path:[[103,30],[103,31]]
     *  type:'',
     *  property:{
     *    status:0,
     *  },
     *  style:{
     *    color:'#ff0000',
     *    size:20,
     *  }
     * }
     * updatePolylines(data)或updatePolylines([data]);
     */
    updatePolyline(data) {
        if (!data) return Promise.resolve();
        try {
            let datas = Array.isArray(data) ? data : [data];
            let formatedDatas = datas.map(item => this._formatUpdatePolyline(item));
            return this.updateFeature(formatedDatas);
        } catch (error) {
            return Promise.reject(error);
        }
    }

    /**
     * 格式化点
     * @param {object} data 点要素对象
     * @param {number} data.lng 经度或横坐标
     * @param {number} data.lat 维度或纵坐标
     * @param {string} data.type 设备类型id
     * @param {object} [data.property] 属性对象
     * @param {object} [data.style] 样式对象
     * @param {string} [data.style.color] 点填充色,16进制颜色值字符串
     * @param {number} [data.style.size] 点大小
     * @param {number} [data.style.zindex] 符号层级
     * @param {boolean} [data.fixed=false] 是否使用矢量缩放,即要素是否按地图单位(米)绘制，否则按像素单位绘制
     * @returns {object} 返回格式化后点对象
     */
    _formatPoint(data) {
        //验证输入参数
        if (!data) throw '点未定义';
        if (!data.lng || typeof data.lng !== 'number') throw '缺少经度';
        if (!data.lat || typeof data.lat !== 'number') throw '缺少纬度';
        if (!data.type || (typeof data.type != 'string' && typeof data.type != 'number')) throw '缺少设备类型';
        let newFixed = (data.fixed === null || data.fixed === undefined || typeof data.fixed !== 'boolean') ? false : data.fixed;
        let newStyle = (data.style === null || data.style === undefined) ? {} : Object.assign({}, data.style);
        // if (newFixed && typeof newStyle.size == 'number') newStyle.size += 'm'; //添加单位米
        if (newFixed && /m/.test(newStyle.size) == false) newStyle.size += 'm'; //添加单位米
        if (newStyle.zindex !== null && newStyle.zindex !== undefined && typeof newStyle.zindex != 'number') throw 'zindex值异常';
        if (newStyle.color !== null && newStyle.color !== undefined) {
            if (/#[0-9a-fA-F][0-9a-fA-F][0-9a-fA-F][0-9a-fA-F][0-9a-fA-F][0-9a-fA-F]/.test(newStyle.color) == false) throw '颜色值异常';
        }
        let newProperty = Object.assign({}, data.property, {
            subtype: data.type,
            zindex: newStyle.zindex,
            size: newStyle.size,
            color: newStyle.color
        });

        let newData = {
            geometry: new Point(data.lng, data.lat),
            properties: newProperty
        }
        return newData;
    }

    /**
     * 格式化线
     * @param {object} data 线要素对象
     * @param {object[]} data.path 坐标点集合,长度大于1
     * @param {number} data.path[][0] 经度或横坐标
     * @param {number} data.path[][1] 维度或纵坐标
     * @param {string} data.type 设备类型id
     * @param {object} [data.property] 属性对象
     * @param {object} [data.style] 样式对象
     * @param {string} [data.style.color] 线颜色,16进制颜色值字符串
     * @param {number} [data.style.size] 线宽
     * @param {boolean} [data.fixed=true] 是否使用矢量缩放,即要素是否按地图单位(米)绘制，否则按像素单位绘制
     * @returns {object} 返回格式化后点对象
     */
    _formatPolyline(data) {
        //验证输入参数
        if (!data) throw '线未定义';
        if (!data.path || !(data.path instanceof Array) || data.path.length < 2) throw '线未定义坐标点集合';
        data.path.forEach(p => {
            if (!(p instanceof Array) || p.length < 2) throw '坐标点缺失';
            p.forEach(value => {
                if (typeof value != 'number') throw '坐标点应该为数字';
            });
        });
        if (!data.type || (typeof data.type != 'string' && typeof data.type != 'number')) throw '缺少设备类型';
        let newFixed = (data.fixed === null || data.fixed === undefined || typeof data.fixed !== 'boolean') ? true : data.fixed;
        let newStyle = (data.style === null || data.style === undefined) ? {} : Object.assign({}, data.style);
        // if (newFixed && typeof newStyle.size == 'number') newStyle.size += 'm'; //添加单位米
        if (newFixed && /m/.test(newStyle.size) == false) newStyle.size += 'm'; //添加单位米
        if (newStyle.zindex !== null && newStyle.zindex !== undefined && typeof newStyle.zindex != 'number') throw 'zindex值异常';
        if (newStyle.color !== null && newStyle.color !== undefined) {
            if (/#[0-9a-fA-F][0-9a-fA-F][0-9a-fA-F][0-9a-fA-F][0-9a-fA-F][0-9a-fA-F]/.test(newStyle.color) == false) throw '颜色值异常';
        }
        let newProperty = Object.assign({}, data.property, {
            subtype: data.type,
            zindex: newStyle.zindex,
            size: newStyle.size,
            color: newStyle.color
        });
        let newData = {
            geometry: new Polyline(data.path),
            properties: newProperty
        }
        return newData;
    }

    /**
     * 格式化点的修改数据
     * @param {object} data 点要素对象
     * @param {string} data.id 要素编号
     * @param {number} [data.lng] 经度或横坐标
     * @param {number} [data.lat] 维度或纵坐标
     * @param {string} [data.type] 设备类型id
     * @param {object} [data.property] 属性对象
     * @param {object} [data.style] 样式对象
     * @param {string} [data.style.color] 点填充色,16进制颜色值字符串
     * @param {number} [data.style.size] 点大小
     * @param {number} [data.style.zindex] 符号层级
     * @param {boolean} [data.fixed=false] 是否使用矢量缩放,即要素是否按地图单位(米)绘制，否则按像素单位绘制
     * @returns {object} 返回格式化后点对象
     */
    _formatUpdatePoint(data) {
        //验证输入参数
        if (!data) throw '点未定义';
        if (data.id === null || data.id === undefined) throw '缺少id';
        if (data.lng != null && data.lng != undefined && typeof data.lng != 'number') throw '经度值异常';
        if (data.lat != null && data.lat != undefined && typeof data.lat != 'number') throw '纬度值异常';
        if (data.type && typeof data.type != 'string' && typeof data.type != 'number') throw '设备类型值异常';
        let newFixed = (data.fixed === null || data.fixed === undefined || typeof data.fixed !== 'boolean') ? false : data.fixed; //没考虑fixed的旧值
        let newStyle = (data.style === null || data.style === undefined) ? {} : Object.assign({}, data.style);
        // if (newFixed && typeof newStyle.size == 'number') newStyle.size += 'm'; //添加单位米
        if (newFixed && /m/.test(newStyle.size) == false) newStyle.size += 'm'; //添加单位米
        else if (newStyle.size != null && newStyle.size != undefined && !(/^[0-9]+(\.[0-9]+)?(m|ft)?$/.test(newStyle.size))) throw 'size值异常';
        if (newStyle.zindex !== null && newStyle.zindex !== undefined && typeof newStyle.zindex != 'number') throw 'zindex值异常';
        if (newStyle.color !== null && newStyle.color !== undefined) {
            if (/#[0-9a-fA-F][0-9a-fA-F][0-9a-fA-F][0-9a-fA-F][0-9a-fA-F][0-9a-fA-F]/.test(newStyle.color) == false) throw '颜色值异常';
        }
        let newProperty = Object.assign({}, data.property, {
            subtype: data.type,
            zindex: newStyle.zindex,
            size: newStyle.size,
            color: newStyle.color
        });

        let newData = {
            id: data.id,
            geometry: (typeof data.lng == 'number' && typeof data.lat == 'number') ? (new Point(data.lng, data.lat)).toGeoJson() : null,
            properties: newProperty
        }
        return newData;
    }

    /**
     * 格式化线的修改数据
     * @param {object} data 线要素对象
     * @param {string} data.id 线要素id
     * @param {object[]} [data.path] 坐标点集合,长度大于1
     * @param {number} [data.path[][0]] 经度或横坐标
     * @param {number} [data.path[][1]] 维度或纵坐标
     * @param {string} [data.type] 设备类型id
     * @param {object} [data.property] 属性对象
     * @param {object} [data.style] 样式对象
     * @param {string} [data.style.color] 线颜色,16进制颜色值字符串
     * @param {number} [data.style.size] 线宽
     * @param {boolean} [data.fixed=true] 是否使用矢量缩放,即要素是否按地图单位(米)绘制，否则按像素单位绘制
     * @returns {object} 返回格式化后点对象
     */
    _formatUpdatePolyline(data) {
        //验证输入参数
        if (!data) throw '线未定义';
        if (data.id === null || data.id === undefined) throw '缺少id';
        if (data.path instanceof Array && data.path.length > 1)
            data.path.forEach(p => {
                if (!(p instanceof Array) || p.length < 2) throw '坐标点缺失';
                p.forEach(value => {
                    if (typeof value != 'number') throw '坐标点应该为数字';
                });
            });
        if (data.type != null && data.type != undefined && typeof data.type != 'string' && typeof data.type != 'number') throw '设备类型值异常';
        let newFixed = (data.fixed === null || data.fixed === undefined || typeof data.fixed !== 'boolean') ? false : data.fixed; //没考虑fixed的旧值
        let newStyle = (data.style === null || data.style === undefined) ? {} : Object.assign({}, data.style);
        // if (newFixed && typeof newStyle.size == 'number') newStyle.size += 'm'; //添加单位米
        if (newFixed && /m/.test(newStyle.size) == false) newStyle.size += 'm'; //添加单位米
        else if (newStyle.size != null && newStyle.size != undefined && !(/^[0-9]+(\.[0-9]+)?(m|ft)?$/.test(newStyle.size))) throw 'size值异常';
        if (newStyle.zindex !== null && newStyle.zindex !== undefined && typeof newStyle.zindex != 'number') throw 'zindex值异常';
        if (newStyle.color !== null && newStyle.color !== undefined) {
            if (/#[0-9a-fA-F][0-9a-fA-F][0-9a-fA-F][0-9a-fA-F][0-9a-fA-F][0-9a-fA-F]/.test(newStyle.color) == false) throw '颜色值异常';
        }
        let newProperty = Object.assign({}, data.property, {
            subtype: data.type,
            zindex: newStyle.zindex,
            size: newStyle.size,
            color: newStyle.color
        });
        let newData = {
            id: data.id,
            geometry: (data.path instanceof Array && data.path.length > 1) ? (new Polyline(data.path)).toGeoJson() : null,
            properties: newProperty
        }
        return newData;
    }

    /**
     * (在gis只有点线图层时)根据几何类型获取图层名
     * @param {string} geotype 几何类型
     */
    _findLayerbyType(layers, geotype) {
        let resultLayer = null;
        layers.forEach(layer => {
            let geotypes = layer.attribute.filter(item => item.name == this.options.GEOField);
            if (geotypes.length == 0) throw '查找GEOField失败';
            else if (geotypes[0].binding.indexOf(geotype) > -1) resultLayer = layer;
        });
        return resultLayer;
    }

    /**
     * 获取图层基础信息
     * @returns {Promise} 返回当前图层组下图层详细信息(包含工作空间、图层名、可见范围、属性字段、子图层、图例、ogc参数)
     * @example
     * let data = await getMapinfo()
     * data={
        "workspace": "demodb3",
        "layers": [
            {
            "visible": true,
            "layertype": "fitting",
            "layername": "节点",
            "extent": {
                "minx": 105.81509954869249,
                "maxx": 105.83560808662894,
                "miny": 32.42409814108862,
                "maxy": 32.44380153427704,
                "crs": "EPSG:4326"
            },
            "attribute": [
                {
                "name": "geom",
                "minOccurs": 0,
                "maxOccurs": 1,
                "nillable": true,
                "binding": "org.locationtech.jts.geom.Point"
                },
                {
                "name": "subtype",
                "minOccurs": 0,
                "maxOccurs": 1,
                "nillable": true,
                "binding": "java.lang.String"
                },
                
            ],
            "legend": "http://192.168.10.222:8080/geoserver/wms?REQUEST=GetLegendGraphic&LEGEND_OPTIONS=DPI:270;forceLabels:off&TRANSPARENT=true&VERSION=1.0.0&FORMAT=image/png&WIDTH=20&HEIGHT=20&LAYER=demodb3:fitting",
            "sublayers": [
                {
                "layername": "警告阀门",
                "title": "警告阀门",
                "visible": true,
                "properties": {
                    "status": "2",
                    "subtype": "20ab312e-d505-4c37-8b85-ac75007e2a31"
                },
                "layertype": "fitting"
                }
            ]
            },
            {
            "visible": true,
            "layertype": "pipe",
            "layername": "管线",
            "extent": {
                "minx": 105.81509056553965,
                "maxx": 105.83556317086473,
                "miny": 32.423988191847684,
                "maxy": 32.44377121009265,
                "crs": "EPSG:4326"
            },
            "attribute": [
                {
                "name": "geom",
                "minOccurs": 0,
                "maxOccurs": 1,
                "nillable": true,
                "binding": "org.locationtech.jts.geom.MultiLineString"
                },
                {
                "name": "status",
                "minOccurs": 0,
                "maxOccurs": 1,
                "nillable": true,
                "binding": "java.lang.String"
                }
            ],
            "legend": "http://192.168.10.222:8080/geoserver/wms?REQUEST=GetLegendGraphic&LEGEND_OPTIONS=DPI:270;forceLabels:off&TRANSPARENT=true&VERSION=1.0.0&FORMAT=image/png&WIDTH=20&HEIGHT=20&LAYER=demodb3:pipe",
            "sublayers": [
                {
                "layername": "失效管道",
                "title": "失效管道",
                "visible": true,
                "properties": {
                    "status": "1",
                    "subtype": "bc22453d-4529-46d2-924b-ac7e00296875"
                },
                "layertype": "pipe"
                }
            ]
            }
        ],
        "ogc": {
            "LAYERS": "demodb3:fitting,demodb3:pipe"
        }
      }
     */
    async getMapinfo() {
        if (this.getMapinfoPromise == null) {
            this.getMapinfoPromise = Init.initialize(this.service, this.workspace);
        }
        return this.getMapinfoPromise;
    }

    /**
     * 使用wfs进行查询，支持空间条件和属性条件
     * @param {object} option 查询条件
     * @param {Geometry} [option.geometry] 空间条件
     * @param {Array} [option.layers] 各图层的属性过滤条件
     * @param {string} [option.layers[].layertype] 图层名
     * @param {object} [option.layers[].where] 图层的属性过滤条件
     * @param {string} [option.layers[].where.key] 图层的属性
     * @param {any} [option.layers[].where.value] 图层的属性值
     * @param {number} [option.tolerance] 基于地图单位的查询容差 
     */
    queryFeature(option) {
        var that = this;
        return new Promise(function (resolve, reject) {
            // 一段耗时的异步操作
            that.getMapinfo().then((mapinfo) => {
                var visibleLayers = mapinfo.layers.filter(layer => layer.visible).map(layer => {
                    return {
                        layertype: layer.layertype,
                        where: null
                    }
                });
                visibleLayers.reverse();
                var newOptions = Object.assign({
                    geometry: null,
                    layers: visibleLayers
                }, option);
                if (visibleLayers.length == 0) throw new Error("缺少查询图层");
                var xmlQuery = that._getGetFeatureXML(newOptions);
                //axios
                var url = that.service + "/wfs?exceptions=json";
                axios.post(url, xmlQuery, {
                    headers: {
                        'Content-Type': 'text/xml'
                    }
                }).then(response => {
                    let filterFeatures = [];
                    //按图层的ids过滤
                    visibleLayers.forEach(layer => { //考虑图层优先级？
                        if (layer.ids == null || typeof layer.ids == 'undefined') {
                            filterFeatures.push.apply(filterFeatures, response.data.features.filter(item => item.id.split('.')[0] == layer.layertype))
                        } else if (layer.ids instanceof Array) filterFeatures.push.apply(filterFeatures, response.data.features.filter(item => layer.ids.includes(item.id)));
                    });
                    //按子图层可见性过滤
                    filterFeatures = filterFeatures.filter(feature => that._validateVisible(mapinfo.layers, feature));
                    let data = Object.assign({}, response.data, {
                        features: filterFeatures,
                        numberMatched: filterFeatures.length,
                        numberReturned: filterFeatures.length,
                        totalFeatures: filterFeatures.length,
                    })
                    resolve({
                        data: data //.data.features
                        //过滤掉图层的ids
                    });
                }).catch(error => {
                    reject(error);
                });
            }).catch((error) => {
                reject(error)
            });
        })
    }

    /**
     * 判断要素的图层可见性
     * @param {object} layers
     * @param {object} feature
     */
    _validateVisible(layers, feature) {
        let result = true;
        if (Object.keys(feature.properties).includes('subtype')) {
            let subtype = feature.properties['subtype'];
            let layertype = feature.id.split('.')[0];
            let sublayers = layers.filter(layer => layer.layertype == layertype)[0].sublayers.filter(sublayer => sublayer.properties != null && sublayer.properties['subtype'] == subtype);
            if (sublayers.length != 0) result = sublayers[0].visible;
            else {
                let otherlayers = layers.filter(layer => layer.layertype == layertype)[0].sublayers.filter(sublayer => sublayer.properties == null)
                if (otherlayers.length > 0) result = otherlayers[0].visible;
            }
        }
        return result;
    }

};

export default WFST